import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Calendar,
  HelpCircle,
  RefreshCw,
  FileText,
  Settings,
  LogOut,
  User,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { LazyProfileEditModal } from '@/components/LazyModals';
import Header from '@/components/Header';

interface MenuItem {
  icon: React.ElementType;
  label: string;
  onClick?: () => void;
  to?: string;
  disabled?: boolean;
}

const MyPage = () => {
  const { profile, user, signOut } = useAuth();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: '로그아웃 완료',
        description: '성공적으로 로그아웃되었습니다.',
      });
    } catch (error) {
      toast({
        title: '로그아웃 실패',
        description: '로그아웃 중 오류가 발생했습니다.',
        variant: 'destructive',
      });
    }
  };

  const handleDisabledClick = () => {
    toast({
      title: '준비 중입니다',
      description: '해당 기능은 곧 제공될 예정입니다.',
    });
  };

  const menuSections = [
    {
      title: '관리',
      items: [
        { icon: Calendar, label: '예약 내역', to: '/booking-history' },
      ] as MenuItem[],
    },
    {
      title: '홀씨 안내',
      items: [
        { icon: HelpCircle, label: '자주 묻는 질문', to: '/faq' },
        { icon: RefreshCw, label: '취소/환불안내', to: '/refund-policy' },
      ] as MenuItem[],
    },
    {
      title: '서비스 약관',
      items: [
        { icon: FileText, label: '이용약관', to: '/terms' },
        { icon: FileText, label: '개인정보처리방침', to: '/privacy' },
        { icon: FileText, label: '위치정보이용약관', to: '/location-terms' },
      ] as MenuItem[],
    },
    {
      title: '설정',
      items: [
        { icon: Settings, label: '프로필 설정', onClick: () => setIsProfileModalOpen(true) },
        { icon: LogOut, label: '로그아웃', onClick: handleSignOut },
      ] as MenuItem[],
    },
  ];

  const MenuItemComponent = ({ item }: { item: MenuItem }) => {
    const Icon = item.icon;
    const content = (
      <>
        <div className="flex items-center space-x-3 flex-1">
          <Icon size={20} className="text-gray-600" />
          <span className="text-gray-700">{item.label}</span>
        </div>
        <ChevronRight size={20} className="text-gray-400" />
      </>
    );

    if (item.disabled) {
      return (
        <button
          onClick={handleDisabledClick}
          className="flex items-center justify-between w-full py-3 px-4 hover:bg-gray-50 transition-colors"
        >
          {content}
        </button>
      );
    }

    if (item.to) {
      return (
        <Link
          to={item.to}
          className="flex items-center justify-between w-full py-3 px-4 hover:bg-gray-50 transition-colors"
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        onClick={item.onClick}
        className="flex items-center justify-between w-full py-3 px-4 hover:bg-gray-50 transition-colors"
      >
        {content}
      </button>
    );
  };

  return (
    <>
      <Header />

      <div className="min-h-screen bg-white md:bg-gray-50 pb-mobile-nav">
        {/* Desktop Container */}
        <div className="max-w-4xl mx-auto md:py-8">
          {/* Profile Section */}
          <div className="bg-white md:rounded-lg md:shadow-sm mb-2 md:mb-4">
            <button
              onClick={() => setIsProfileModalOpen(true)}
              className="flex items-center justify-between w-full p-4 md:p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Avatar className="h-12 w-12 md:h-16 md:w-16">
                  {(profile?.avatar_url || user?.user_metadata?.avatar_url) && (
                    <AvatarImage
                      src={profile?.avatar_url || user?.user_metadata?.avatar_url}
                      alt={profile?.name || '프로필'}
                    />
                  )}
                  <AvatarFallback className="bg-[#F39800] text-white text-lg md:text-xl">
                    {profile?.name ? profile.name.charAt(0).toUpperCase() : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <p className="text-lg md:text-xl font-semibold text-gray-900">
                    {profile?.name || '사용자'}
                  </p>
                  <p className="text-sm text-gray-500">{profile?.email || user?.email}</p>
                </div>
              </div>
              <ChevronRight size={24} className="text-gray-400" />
            </button>
          </div>

          <Separator className="my-2 md:hidden" />

          {/* Menu Sections */}
          {menuSections.map((section, sectionIndex) => (
            <div key={section.title} className="bg-white md:rounded-lg md:shadow-sm mb-2 md:mb-4">
              <div className="py-3 px-4 md:px-6">
                <h2 className="text-sm font-semibold text-gray-900 mb-1">{section.title}</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {section.items.map((item, itemIndex) => (
                  <MenuItemComponent key={`${sectionIndex}-${itemIndex}`} item={item} />
                ))}
              </div>
            </div>
          ))}

          {/* Company Info */}
          <div className="bg-white md:rounded-lg md:shadow-sm p-4 md:p-6 mb-20 md:mb-0">
            <div className="flex items-center space-x-2 mb-4">
              <img src="/images/logo.svg" alt="홀씨" className="h-6 w-auto" />
              <span className="text-lg font-bold text-gray-900">홀씨 트레킹</span>
            </div>

            <div className="space-y-2 text-sm text-gray-600">
              <p className="font-semibold text-gray-900">(주)닷옴</p>
              <div className="flex items-start space-x-2">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <span>경기도 남양주시 다산동 6614-1, 다산 현대 프리미어 캠퍼스몰 A동 8층 39호</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone size={16} />
                <span>010-3741-2556</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail size={16} />
                <span>support@wholsee.kr</span>
              </div>

              <div className="pt-3 border-t border-gray-200 mt-4 space-y-1">
                <p>
                  <span className="font-medium">대표:</span> 박영수
                </p>
                <p>
                  <span className="font-medium">사업자등록번호:</span> 874-88-02713
                </p>
                <p>
                  <span className="font-medium">통신판매업신고번호:</span> 제2024-서울강남-0000호
                </p>
                <p>
                  <span className="font-medium">관광사업등록번호:</span> 제0000-000호
                </p>
              </div>

              <div className="pt-3 border-t border-gray-200 mt-4">
                <p className="text-xs text-gray-500">© 2025 (주)닷옴. All rights reserved.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <LazyProfileEditModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />
    </>
  );
};

export default MyPage;
